import React, { useRef, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import apiClient from '../../../utils/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Upload, Image, Video, Plus, Loader2, Trash2, GripVertical } from 'lucide-react';

const DraggableAsset = ({ asset, children }) => {
  const trackData = {
    type: asset.asset_type,
    asset_id: asset.asset_id,
    source_url: asset.proxy_url ? `${process.env.REACT_APP_BACKEND_URL}${asset.proxy_url}` : null,
    duration: asset.asset_type === 'video' ? Math.min(asset.duration || 5, 60) : 3,
    animation: asset.asset_type === 'photo' ? 'zoom-in' : 'none',
    transition: 'fade',
    trim_start: 0,
    trim_end: asset.duration || 0,
  };

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `asset-${asset.asset_id}`,
    data: { type: 'asset', track: trackData, asset },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      className="relative"
    >
      <div {...listeners} className="absolute left-0 top-0 bottom-0 w-6 flex items-center justify-center cursor-grab active:cursor-grabbing z-10">
        <GripVertical className="h-3 w-3 text-muted-foreground" />
      </div>
      {children}
    </div>
  );
};

export const AssetPanel = ({ assets, tracks, onAssetsChange, onAddTrack }) => {
  const photoInputRef = useRef();
  const videoInputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file, assetType) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('asset_type', assetType);
      const res = await apiClient.post('/reels/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${assetType === 'video' ? 'Video' : 'Photo'} uploaded`);
      onAssetsChange();
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await handleUpload(file, 'photo');
    e.target.value = '';
  };

  const handleVideoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await handleUpload(file, 'video');
    e.target.value = '';
  };

  const handleDeleteAsset = async (assetId) => {
    try {
      await apiClient.delete(`/reels/assets/${assetId}`);
      toast.success('Asset deleted');
      onAssetsChange();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="w-56 border border-border/50 rounded-lg bg-card p-3 flex flex-col gap-3 overflow-y-auto" data-testid="asset-panel">
      <h3 className="text-sm font-semibold">Assets</h3>

      {/* Upload buttons */}
      <div className="flex gap-2">
        <Button
          variant="outline" size="sm" className="flex-1 text-xs"
          onClick={() => photoInputRef.current?.click()}
          disabled={uploading}
          data-testid="upload-photo-btn"
        >
          {uploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Image className="h-3 w-3 mr-1" />}
          Photo
        </Button>
        <Button
          variant="outline" size="sm" className="flex-1 text-xs"
          onClick={() => videoInputRef.current?.click()}
          disabled={uploading}
          data-testid="upload-video-btn"
        >
          {uploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Video className="h-3 w-3 mr-1" />}
          Video
        </Button>
      </div>
      <input ref={photoInputRef} type="file" accept="image/*" hidden onChange={handlePhotoChange} />
      <input ref={videoInputRef} type="file" accept="video/*" hidden onChange={handleVideoChange} />

      {/* Asset library — drag items to timeline */}
      <p className="text-[10px] text-muted-foreground">Drag assets to the timeline</p>
      <div className="space-y-1">
        {assets.filter(a => a.asset_type !== 'audio').map((asset) => (
          <DraggableAsset key={asset.asset_id} asset={asset}>
            <div className="flex items-center gap-2 p-1.5 pl-6 bg-muted/20 rounded text-xs group" data-testid={`asset-item-${asset.asset_id}`}>
              <div className="w-8 h-8 rounded bg-muted/40 flex items-center justify-center flex-shrink-0">
                {asset.asset_type === 'video' ? <Video className="h-3 w-3" /> : <Image className="h-3 w-3" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="truncate">{asset.filename || asset.asset_id}</p>
                {asset.duration && <p className="text-muted-foreground">{asset.duration.toFixed(1)}s</p>}
              </div>
              <Button
                variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100"
                onClick={() => {
                  onAddTrack({
                    type: asset.asset_type,
                    asset_id: asset.asset_id,
                    source_url: asset.proxy_url ? `${process.env.REACT_APP_BACKEND_URL}${asset.proxy_url}` : null,
                    duration: asset.asset_type === 'video' ? Math.min(asset.duration || 5, 60) : 3,
                    animation: asset.asset_type === 'photo' ? 'zoom-in' : 'none',
                    transition: 'fade',
                    trim_start: 0,
                    trim_end: asset.duration || 0,
                  });
                }}
              >
                <Plus className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 text-destructive"
                onClick={() => handleDeleteAsset(asset.asset_id)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </DraggableAsset>
        ))}
        {assets.filter(a => a.asset_type !== 'audio').length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">
            Upload photos or videos to get started
          </p>
        )}
      </div>
    </div>
  );
};
